import { PixelEvents, PixelAudiences, PIXEL_EVENT_URL, DEPOSITS_KEYS } from '@skeleton/consts/pixel';
import type { PixelEventName } from '@skeleton/consts/pixel';

export default defineNuxtPlugin(nuxtApp => {
  const {
    public: { pixelId },
  } = useRuntimeConfig();

  const _loadPixel = (): Promise<void> => {
    return new Promise((resolve, reject) => {
      if (document.querySelector(`script[src="${PIXEL_EVENT_URL}"]`)) {
        return resolve();
      }

      const script = document.createElement('script');
      script.src = `${PIXEL_EVENT_URL}`;

      script.onload = () => {
        resolve();
      };

      script.onerror = () => {
        reject('Failed to load Pixel');
      };

      document.body.appendChild(script);
    });
  };

  const callPixelEvent = (eventName: PixelEventName) => {
    try {
      window.callAction?.(eventName);
    } catch (error) {
      console.error('Error calling pixel event:', error);
    }
  };

  const sendAudienceData = (eventName: PixelEventName) => {
    const audienceId = PixelAudiences[eventName];

    const wn = window.navigator;
    const ws = window.screen;
    const z = 'WEBGL';
    let hc = 0;
    let vc = '';

    try {
      hc = wn.hardwareConcurrency;
    } catch (p) {}

    try {
      const ca = document.createElement('canvas');
      const gl = ca.getContext('experimental-' + z.toLowerCase()) as WebGLRenderingContext;
      vc = escape(gl.getParameter(gl.getExtension(z + '_debug_renderer_info')['UNMASKED_RENDERER_' + z]));
    } catch (p) {}

    const p: Record<string, string | number | undefined> = {
      sr: `${ws.width}x${ws.height}`,
      tz: -new Date().getTimezoneOffset(),
      pl: wn.plugins?.length ?? 0,
      mi: wn.mimeTypes?.length ?? 0,
      me: wn.deviceMemory,
      hc: hc,
      vc: vc,
    };

    let q = `https://rtb.dspretargettraff.com/au?a=${audienceId}&`;

    for (const k in p) {
      const v = p[k];
      if (v && v !== 'undefined') {
        q += `&${k}=${v}`;
      }
    }

    new Image().src = q;
  };

  const sendFullEvent = (eventName: PixelEventName) => {
    callPixelEvent(eventName);
    sendAudienceData(eventName);
  };

  const initPixel = async (eventName?: PixelEventName) => {
    if (!pixelId) return;

    const oldScript = document.querySelector(`script[src="${PIXEL_EVENT_URL}"]`);

    if (oldScript) {
      oldScript.remove();
    }

    window.AU = undefined;
    window.callAction = undefined;

    const profileStore = useProfileStore();

    const acl = Object.entries(PixelEvents).reduce(
      (acc, [key, value]) => {
        acc[key] = `${value}&extID=${profileStore.profile?.id || 'undefined'}`;
        return acc;
      },
      {} as Record<string, string>
    );

    window.AU = { aid: (pixelId as string) || '', acl };

    try {
      await _loadPixel();

      window.callAction = a => {
        if (window.AU?.action) {
          window.AU.action(a);
        } else {
          console.error('Error: Pixel action is not defined');
        }
      };

      sendFullEvent(eventName ?? 'visit');
      profileStore.sendPixelEventToAnalytics('init');
    } catch (e) {
      console.error('Error: load pixel');
    }
  };

  nuxtApp.hook('app:mounted', async () => {
    await initPixel();

    useListen('pixelEvent', eventName => {
      if (!pixelId) return;

      sendFullEvent(eventName);

      if (DEPOSITS_KEYS.includes(eventName)) {
        callPixelEvent('deposit');
        sendAudienceData('deposit');
      }

      if (eventName === 'bet') {
        sendAudienceData('bet30');
      }
    });
  });

  return {
    provide: {
      pixel: {
        init: initPixel,
      },
    },
  };
});
